import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowRight, BellRing, CheckCircle2, Clock, FileText, Flame, Loader2,
  Mail, PlusCircle, RefreshCw, Send, Users,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const t = localStorage.getItem("grc_admin_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

const SEVERITY = {
  high: { ring: "border-l-4 border-l-red-400", chip: "bg-red-50 text-red-700", label: "Needs you now" },
  medium: { ring: "border-l-4 border-l-amber-400", chip: "bg-amber-50 text-amber-700", label: "This week" },
  low: { ring: "border-l-4 border-l-teal", chip: "bg-teal-light text-teal", label: "When you can" },
};

const Pulse = ({ label, value, hint, icon: Icon, testid }) => (
  <div className="rounded-2xl bg-white border border-linen-border p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-ink-500">
      <Icon size={14} className="text-teal" />
      <span className="text-[10px] font-bold uppercase tracking-[0.16em]">{label}</span>
    </div>
    <div className="mt-2 text-2xl font-heading font-medium text-navy leading-none">{value}</div>
    <div className="mt-1 text-[11px] text-ink-500 leading-snug">{hint}</div>
  </div>
);

const Launcher = ({ icon: Icon, label, hint, onClick, busy, testid }) => (
  <button
    onClick={onClick}
    disabled={busy}
    data-testid={testid}
    className="group text-left rounded-2xl bg-white border border-linen-border hover:border-teal p-4 transition-all hover:-translate-y-0.5 disabled:opacity-60"
  >
    <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-navy text-lime">
      {busy ? <Loader2 size={16} className="animate-spin" /> : <Icon size={16} />}
    </span>
    <div className="mt-3 font-semibold text-sm text-navy flex items-center gap-1.5">
      {label}
      <ArrowRight size={13} className="text-teal opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
    <div className="mt-0.5 text-[11px] text-ink-500 leading-snug">{hint}</div>
  </button>
);

export const CommandCenter = ({ onGoToTab }) => {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState("");

  const load = useCallback(
    () => api.get("/admin/command-center").then((r) => setData(r.data)).catch(() => setData({ needs: [], pulse: {} })),
    [],
  );
  useEffect(() => { load(); }, [load]);

  const runAlerts = async () => {
    setRunning("alerts");
    try {
      const { data: res } = await api.post("/admin/pipeline-alerts/run?officers=true");
      toast.success(res?.sent ? `Alerts sent for ${res.sent} applicant${res.sent !== 1 ? "s" : ""}` : "Nothing waiting — no alerts needed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run alerts.");
    } finally {
      setRunning("");
    }
  };

  if (!data) {
    return (
      <div className="rounded-3xl bg-white border border-linen-border p-8 flex items-center gap-2 text-ink-500" data-testid="command-center-loading">
        <Loader2 className="animate-spin" size={16} /> Reading the room…
      </div>
    );
  }

  const { needs = [], pulse = {}, latest_field_update: fu } = data;

  return (
    <div className="space-y-6" data-testid="command-center">
      {/* Needs you */}
      <div className="rounded-3xl bg-white border border-linen-border shadow-soft overflow-hidden">
        <div className="px-6 py-5 border-b border-linen-border flex items-center justify-between gap-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal">Today</div>
            <h2 className="mt-1 font-heading font-medium text-2xl text-navy">
              {needs.length === 0 ? "Nothing is waiting on you." : `${needs.length} thing${needs.length !== 1 ? "s" : ""} need a decision`}
            </h2>
          </div>
          <button onClick={load} data-testid="command-center-refresh" className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500 hover:text-navy transition-colors">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {needs.length === 0 ? (
          <div className="px-6 py-10 flex items-start gap-3" data-testid="command-center-clear">
            <CheckCircle2 size={22} className="text-lime shrink-0" />
            <div>
              <p className="text-navy font-semibold">Applicants answered, leads picked up, this week's update sent.</p>
              <p className="text-sm text-ink-500 mt-1">Good day to send the cadence link to a brokerage owner.</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-linen-border">
            {needs.map((n) => {
              const s = SEVERITY[n.severity] || SEVERITY.low;
              return (
                <div key={n.key} className={`px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 ${s.ring}`} data-testid={`need-${n.key}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${s.chip}`}>
                        {s.label}
                      </span>
                      <span className="font-semibold text-navy">{n.title}</span>
                    </div>
                    <p className="mt-1 text-[13px] text-ink-600 leading-relaxed">{n.detail}</p>
                  </div>
                  <button
                    onClick={() => onGoToTab(n.target)}
                    data-testid={`need-cta-${n.key}`}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-navy hover:bg-navy-light text-white font-semibold text-xs px-4 py-2 transition-colors"
                  >
                    {n.cta} <ArrowRight size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Launchers */}
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal">Fire something off</div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Launcher icon={Send} label="Write the Field Update" hint="This week's video, brief, script and action item" onClick={() => onGoToTab("comms")} testid="launch-field-update" />
          <Launcher
            icon={Mail}
            label={fu ? `Resend "${fu.subject}"` : "Resend a past update"}
            hint={
              fu
                ? `Sent to ${fu.recipients}${fu.tracked > 0 ? ` · ${fu.opened} of ${fu.tracked} opened` : ""} · same email, same page`
                : "Same email and web page, more recipients"
            }
            onClick={() => onGoToTab("comms")}
            testid="launch-resend"
          />
          <Launcher icon={BellRing} label="Run applicant alerts" hint="Nudge yourself and officers about who's waiting" onClick={runAlerts} busy={running === "alerts"} testid="launch-alerts" />
          <Launcher icon={PlusCircle} label="Add an officer" hint="New MLO page, account and links" onClick={() => onGoToTab("mlos")} testid="launch-add-mlo" />
        </div>
      </div>

      {/* Pulse */}
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal">Last 7 days</div>
        <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Pulse icon={Users} label="Consumer leads" value={pulse.leads_7d ?? 0} hint="People who asked us to check their rate" testid="pulse-leads" />
          <Pulse icon={Clock} label="Partner applications" value={pulse.applications_7d ?? 0} hint="New partner applications submitted" testid="pulse-apps" />
          <Pulse icon={FileText} label="Applications started" value={pulse.apps_started_7d ?? 0} hint="Consumers handed off to the lender with a reference code" testid="pulse-started" />
          <Pulse icon={Flame} label="Active partners" value={`${pulse.active_partners ?? 0}/${pulse.partners_total ?? 0}`} hint="Partners who logged activity this month" testid="pulse-active" />
        </div>
      </div>
    </div>
  );
};

export default CommandCenter;
